import { useEffect, useState } from "preact/hooks";
import { ApiError } from "@tenergy/web-core/api";
import { requestIdOf, slugOf } from "./api/request.js";

/**
 * Toasts: bottom-right, 4 s, `save` for success and `danger` for failure (site-design.md §8).
 *
 * A FAILURE NAMES THE API'S SLUG AND THE REQUEST ID. That pair is what finds the request in
 * the API log; a toast without it is a message the operator can only retype into a ticket.
 * Failures stay until closed — 4 s is too short to copy an id from.
 *
 * A module-level list with subscribers rather than a context: a mutation fires a toast from
 * wherever it resolves, and the shell renders the stack once.
 */

export interface Toast {
  id: number;
  tone: "success" | "error";
  title: string;
  detail: string | null;
  slug: string | null;
  requestId: string | null;
}

const DURATION_MS = 4_000;
let toasts: Toast[] = [];
let nextId = 1;
const listeners = new Set<(list: Toast[]) => void>();

function emit(): void {
  for (const listener of listeners) listener(toasts);
}

function push(toast: Omit<Toast, "id">): void {
  const id = nextId++;
  toasts = [...toasts.slice(-3), { ...toast, id }];
  emit();
  if (toast.tone === "success") setTimeout(() => dismissToast(id), DURATION_MS);
}

export function dismissToast(id: number): void {
  toasts = toasts.filter((toast) => toast.id !== id);
  emit();
}

export function toastSuccess(title: string, detail: string | null = null): void {
  push({ tone: "success", title, detail, slug: null, requestId: null });
}

export function toastError(title: string, error: unknown): void {
  // A refusal that names a field is already shown under that field; the toast then names the
  // field and leaves the sentence where the operator can fix it.
  const field = error instanceof ApiError ? error.field : null;
  push({
    tone: "error",
    title,
    detail:
      field !== null
        ? `The API refused the field “${field}”.`
        : error instanceof Error
          ? error.message
          : error === undefined
            ? null
            : String(error),
    slug: slugOf(error),
    requestId: requestIdOf(error),
  });
}

export function Toaster() {
  const [list, setList] = useState<Toast[]>(toasts);
  useEffect(() => {
    listeners.add(setList);
    return () => {
      listeners.delete(setList);
    };
  }, []);
  if (list.length === 0) return null;
  return (
    <div
      class="pointer-events-none fixed right-4 bottom-4 z-50 flex w-[360px] max-w-[calc(100vw-2rem)] flex-col gap-2"
      aria-live="polite"
    >
      {list.map((toast) => (
        <div
          key={toast.id}
          role="status"
          class={`toast-in pointer-events-auto rounded-[12px] border bg-surface-raised px-3 py-2.5 shadow-lg shadow-black/40 ${
            toast.tone === "error" ? "border-danger/50" : "border-save/40"
          }`}
        >
          <div class="flex items-start gap-2">
            <span
              aria-hidden="true"
              class={`mt-1 size-2 shrink-0 rounded-full ${toast.tone === "error" ? "bg-danger" : "bg-save"}`}
            />
            <div class="min-w-0 flex-1">
              <div class="text-[12px] font-semibold text-ink">{toast.title}</div>
              {toast.detail === null ? null : (
                <div class="mt-0.5 text-[11px] leading-relaxed text-ink-muted">{toast.detail}</div>
              )}
              {toast.slug === null && toast.requestId === null ? null : (
                <div class="num mt-1 text-[10.5px] break-all text-ink-muted">
                  {toast.slug === null ? null : <span class="text-danger">{toast.slug}</span>}
                  {toast.slug !== null && toast.requestId !== null ? " · " : null}
                  {toast.requestId === null ? null : <span>{toast.requestId}</span>}
                </div>
              )}
            </div>
            <button
              type="button"
              aria-label="Dismiss"
              class="-mt-0.5 shrink-0 rounded px-1 text-[13px] text-ink-muted hover:text-ink"
              onClick={() => dismissToast(toast.id)}
            >
              ×
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Test seam: forget every toast. */
export function resetToasts(): void {
  toasts = [];
  emit();
}
